import type { CSSProperties } from 'react'

import { cn } from '@/shared/lib/cn'
import { useI18n, type I18n } from '@/shared/i18n'
import {
  DUE_STATUS_ICON,
  DUE_STATUS_VAR,
  getDaysUntilDue,
  getDueStatus,
  type DueDateInput,
  type DueStatus,
} from '@/shared/lib/due-date'

export interface DueDateBadgeProps {
  date: DueDateInput
  isDone?: boolean
  now?: Date
  timeZone?: string
  soonThresholdDays?: number
  /** `relative` (hace 3 dias) o `absolute` (17 sep 2026). */
  display?: 'relative' | 'absolute'
  className?: string
}

/**
 * "Vencida" es punto rojo + `!` + "hace 3 días": color, ícono y texto, los tres
 * siempre. Nunca solo color.
 *
 * Los días y las semanas salen por `count`, que es lo que hace que CLDR elija
 * la forma: "Hace 1 día" contra "Hace 3 días", y "{count} day ago" contra
 * "{count} days ago", que en inglés ni siquiera pone el número en el mismo
 * lugar de la frase. Un ternario `días/día` acá habría funcionado en español y
 * roto en cuanto alguien tradujera.
 */
/**
 * Pasado este horizonte, la fecha relativa deja de decir algo y se muestra la
 * absoluta.
 *
 * Sin tope, una fecha en 2999 se leía **"En 50784 semanas"** y una de 1901,
 * "Hace 45851 días" (QA funcional 6.3). Son textos que nadie escribiría: a esa
 * distancia el número no se puede imaginar y lo único útil es la fecha. Un año
 * es el corte natural — "en 8 meses" todavía se entiende; "en 60 semanas" ya
 * no.
 *
 * El tope es sobre el TEXTO, no sobre el dato: el vencimiento sigue guardado y
 * el color del estado se calcula igual.
 */
const HORIZONTE_RELATIVO_DIAS = 365

function relativeLabel(
  t: I18n['t'],
  formatDate: I18n['formatDate'],
  status: DueStatus,
  days: number | null,
  date: DueDateInput,
  timeZone?: string,
): string {
  if (status === 'none' || days === null) return t('due.status.none')
  if (status === 'done') return t('due.status.done')
  if (Math.abs(days) > HORIZONTE_RELATIVO_DIAS) return formatDate(date, timeZone)
  if (days === 0) return t('due.relative.today')
  if (days === 1) return t('due.relative.tomorrow')
  if (days === -1) return t('due.relative.yesterday')
  if (days < 0) return t('due.relative.daysAgo', { count: Math.abs(days) })
  if (days <= 7) return t('due.relative.inDays', { count: days })
  if (days <= 14) return t('due.relative.inWeeks', { count: 2 })
  return t('due.relative.inWeeks', { count: Math.round(days / 7) })
}

export function DueDateBadge({
  date,
  isDone,
  now,
  timeZone,
  soonThresholdDays,
  display = 'relative',
  className,
}: DueDateBadgeProps) {
  const { t, formatDate } = useI18n()
  const options = { now, timeZone, soonThresholdDays, isDone }
  const status = getDueStatus(date, options)
  const days = getDaysUntilDue(date, options)

  const style: CSSProperties = { color: `hsl(var(${DUE_STATUS_VAR[status]}))` }
  const absolute = formatDate(date, timeZone)
  const label =
    display === 'absolute' ? absolute : relativeLabel(t, formatDate, status, days, date, timeZone)

  return (
    <span
      style={style}
      title={status === 'none' ? t('due.noDueDate') : absolute}
      data-due-status={status}
      className={cn(
        'num inline-flex items-center gap-1 text-xs whitespace-nowrap',
        status === 'overdue' && 'font-medium',
        status === 'none' && 'text-text-3',
        className,
      )}
    >
      <span aria-hidden="true" className="leading-none">
        {DUE_STATUS_ICON[status]}
      </span>
      {label}
    </span>
  )
}
